/**
 * How full the model's context window is — a ring and a percentage beside the
 * composer. A port of `bimgo-ai-web`'s `ContextMeter`, tiers and reasoning
 * intact.
 *
 * Deliberately persistent rather than a banner that appears at thresholds: a
 * warning that only shows up when it is already a problem interrupts the
 * conversation and leaves no way to check where you stand in between.
 *
 * Escalation is carried by one mechanism — the pill behind the readout. Bare
 * and quiet while the window is mostly empty, grey once half is gone, amber as
 * it tightens, red once the next message may simply not send. The remedy lives
 * in the tooltip so the row's width does not jump when a thread crosses a tier.
 *
 * Renders nothing when there is no reading: no turn has completed, or the model
 * publishes no window. With no denominator there is no percentage, and this
 * does not guess one — a local `chars ÷ 4` estimate under-counts Traditional
 * Chinese by ~3.5x, which would invert the warning for the users this ships for.
 */
import { useLocale } from "../../i18n/LocaleProvider";
import {
  CONTEXT_USAGE_TIERS,
  contextUsageLevel,
  contextUsagePercent,
  type ContextUsage,
} from "../../lib/chat";

const RADIUS = 6;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;

export function ContextMeter({ usage }: { usage: ContextUsage | null }) {
  const { t } = useLocale();

  const percent = contextUsagePercent(usage);
  if (percent === null) return null;

  const level = contextUsageLevel(percent);
  const tooltip = t(
    percent >= CONTEXT_USAGE_TIERS.critical
      ? "chat.contextMeterCriticalTooltip"
      : "chat.contextMeterTooltip",
    { percent: String(percent) },
  );

  return (
    <div
      className={`context-meter is-${level}`}
      title={tooltip}
      aria-label={`${t("chat.contextMeterLabel")} ${percent}%`}
    >
      {/* Rotated so the arc starts at 12 o'clock rather than 3. */}
      <svg viewBox="0 0 16 16" width="16" height="16" aria-hidden="true">
        <circle className="context-meter-track" cx="8" cy="8" r={RADIUS} fill="none" strokeWidth="2.5" />
        <circle
          className="context-meter-arc"
          cx="8"
          cy="8"
          r={RADIUS}
          fill="none"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeDasharray={`${(CIRCUMFERENCE * percent) / 100} ${CIRCUMFERENCE}`}
        />
      </svg>
      <span className="context-meter-value">{percent}%</span>
    </div>
  );
}
